import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from '../hooks/useAuth';
import { useCallback, useRef } from 'react';
import API_CONFIG from '../config/apiConfig';
import { auth0Config } from '../config/auth0Config';

const CSRF_EXEMPT_PATHS = [
  '/csrf/token',
  '/auth/signin',
  '/auth/signout',
  'auth/resend_verification_email'
];

export const useApiClient = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { sessionId } = useAuth();
  const sessionIdRef = useRef(sessionId);
  const csrfTokenPromiseRef = useRef(null);

  sessionIdRef.current = sessionId;

  const getAuthHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently({
      audience: auth0Config.authorizationParams.audience,
      scope: 'openid profile email offline_access',
    });

    const headers = {
      'Authorization': `Bearer ${token}`,
    };

    if (sessionIdRef.current) {
      headers['X-Session-Id'] = sessionIdRef.current;
    }

    return headers;
  }, [getAccessTokenSilently]);

  const getCsrfToken = useCallback(async () => {
    if (!csrfTokenPromiseRef.current) {
      csrfTokenPromiseRef.current = (async () => {
        try {
          const authHeaders = await getAuthHeaders();
          const url = `${API_CONFIG.BASE_URL}/csrf/token`;
          const response = await fetch(url, {
            method: 'GET',
            headers: authHeaders,
          });

          if (!response.ok) {
            throw new Error(`Failed to get CSRF token: ${response.status}`);
          }

          const data = await response.json();
          if (!data?.csrf_token) {
            throw new Error('CSRF token missing in response');
          }
          return data.csrf_token;
        } catch (error) {
          csrfTokenPromiseRef.current = null;
          throw error;
        }
      })();
    }
    return await csrfTokenPromiseRef.current || '';
  }, [getAuthHeaders]);

  const apiFetch = useCallback(async (endpoint, options = {}) => {
    const authHeaders = await getAuthHeaders();
    const url = `${API_CONFIG.BASE_URL}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        ...authHeaders,
        ...options.headers,
      },
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
      }

      if (response.status === 401) {
        console.warn('Session expired (401). Redirecting to login...');
        window.dispatchEvent(new CustomEvent('auth:unauthorized', {
          detail: {
            status: 401,
            message: 'Session expired. Please log in again.'
          }
        }));
      }

      throw new Error(errorData.error?.message || errorData.message || 'API request failed');
    }

    return response;
  }, [getAuthHeaders]);

  const apiGet = useCallback(async (endpoint, options = {}) => {
    return apiFetch(endpoint, {
      ...options,
      method: 'GET',
    });
  }, [apiFetch]);

  const apiPost = useCallback(async (endpoint, body, options = {}) => {
    const isFormData = body instanceof FormData;
    const isBinaryData = body instanceof Uint8Array || 
                        body instanceof ArrayBuffer || 
                        body instanceof Blob;
    const normalizedPath = endpoint.toLowerCase();
    const isExemptPath = CSRF_EXEMPT_PATHS.some(exemptPath => 
      normalizedPath.includes(exemptPath.toLowerCase())
    );
    const headers = {};
    
    if (isFormData) {
    } else if (isBinaryData) {
      headers['Content-Type'] = options.headers?.['Content-Type'] || 'application/pdf';
    } else {
      headers['Content-Type'] = 'application/json';
    }

    if (!isExemptPath) {
      const csrfToken = await getCsrfToken();
      headers['X-CSRF-Token'] = csrfToken;
    }

    const finalHeaders = { ...headers, ...options.headers };
    if (isFormData && 'Content-Type' in finalHeaders) {
      delete finalHeaders['Content-Type'];
    }

    let requestBody;
    if (isFormData) {
      requestBody = body;
    } else if (isBinaryData) {
      requestBody = body;
    } else {
      requestBody = JSON.stringify(body);
    }

    return apiFetch(endpoint, {
      ...options,
      method: 'POST',
      headers: finalHeaders,
      body: requestBody,
    });
  }, [apiFetch, getCsrfToken]);

  const apiFetchWithCsrf = useCallback(async (endpoint, options = {}) => {
    const method = String(options.method || 'GET').toUpperCase();
    const normalizedPath = endpoint.toLowerCase();
    const isExemptPath = CSRF_EXEMPT_PATHS.some((exemptPath) =>
      normalizedPath.includes(exemptPath.toLowerCase())
    );

    const headers = { ...(options.headers || {}) };
    const isStateChangeMethod = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
    if (isStateChangeMethod && !isExemptPath) {
      const csrfToken = await getCsrfToken();
      headers['X-CSRF-Token'] = csrfToken;
    }

    return apiFetch(endpoint, {
      ...options,
      method,
      headers,
    });
  }, [apiFetch, getCsrfToken]);

  return {
    apiFetch,
    apiGet,
    apiPost,
    apiFetchWithCsrf,
    getAuthHeaders,
  };
};
